import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import CopyButton from '../../components/ui/CopyButton.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { EmptyState, Facts, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { checkRequired } from '../../utils/validate.js';
import { fmtDateTime, fmtDay } from '../../utils/format.js';
import { ApprovedSendersField } from './approvedSenders.jsx';

// Tab-separated pastes straight into Excel or Google Sheets as columns
const tsvOf = (contacts) => [['First name', 'Last name', 'Job title', 'Company', 'Email'], ...contacts.map((c) => [c.first_name, c.last_name, c.job_title, c.company, c.address])]
  .map((r) => r.join('\t')).join('\n');

const csvOf = (contacts) => {
  const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [['First name', 'Last name', 'Job title', 'Company', 'Email'], ...contacts.map((c) => [c.first_name, c.last_name, c.job_title, c.company, c.address])]
    .map((r) => r.map(q).join(',')).join('\n');
};

function EditModal({ base, planting, kinds, onClose, onSaved }) {
  const [form, setForm] = useState({ given_to: planting.given_to, given_to_kind: planting.given_to_kind, allowed_domains: planting.allowed_domains.join('\n'), note: planting.note || '' });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm({ ...form, [k]: v }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const save = async () => {
    const e = checkRequired(form.given_to, 'who the list went to', { max: 200 });
    if (e) { setErrors({ given_to: e }); return; }
    setBusy(true); setError('');
    try { await api(base, { method: 'PATCH', body: form }); onSaved(); } catch (err) { setErrors(err.data?.fields || {}); setError(err.data?.fields ? '' : err.message); setBusy(false); }
  };
  return (
    <Modal title="Edit details" onClose={onClose} onSubmit={save} busy={busy}
      footer={<ModalActions submitLabel="Save" busyLabel="Saving…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      {planting.purpose === 'own_lists' ? (
        <Field label="Which list or system" required maxLength={200} value={form.given_to} onChange={(e) => set('given_to', e.target.value)} error={errors.given_to} />
      ) : (
        <div className="row-2">
          <Field label="Given to" required maxLength={200} value={form.given_to} onChange={(e) => set('given_to', e.target.value)} error={errors.given_to} />
          <Field label="They are" as="select" value={form.given_to_kind} onChange={(e) => set('given_to_kind', e.target.value)}>
            {Object.entries(kinds).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </Field>
        </div>
      )}
      <ApprovedSendersField own={planting.purpose === 'own_lists'} value={form.allowed_domains}
        onChange={(e) => set('allowed_domains', e.target.value)} error={errors.allowed_domains} />
      <Field label="Note" optional maxLength={500} value={form.note} onChange={(e) => set('note', e.target.value)} />
    </Modal>
  );
}

/** One list shared with someone: its planted contacts and every email they received. */
export default function Planting() {
  const { eventId, plantingId } = useParams();
  const location = useLocation();
  const { organisation } = useAuth();
  const base = `/portal/organisations/${organisation.id}/plantings/${plantingId}`;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);   // edit | retire | reactivate
  const [notice, setNotice] = useState(location.state?.created
    ? (location.state.own ? 'Done. Add these contacts to that list now, like any other attendee. From then on we receive your emails to it too.' : 'Done. Add these contacts to the list before you share it — scattered through it, not all at the end.')
    : '');

  const load = useCallback(async () => {
    try { setData(await api(base)); } catch (err) { setError(err.status === 404 ? 'This list isn’t here.' : err.message); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const back = { to: `/events/${eventId}`, label: 'Event' };
  if (error) return <div className="page"><PageHeader back={back} title="Planted contacts" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;
  const { planting: p, contacts, hits } = data;
  const leaks = hits.filter((h) => !h.expected);
  const stopped = p.status !== 'active';
  const own = p.purpose === 'own_lists';

  return (
    <div className="page">
      <PageHeader back={back} title={own ? `Your list: ${p.given_to}` : `List given to ${p.given_to}`}
        meta={<>{stopped ? <Pill>Stopped</Pill> : <Pill tone="good">Watching</Pill>}<span className="muted">{own ? 'Your own list' : p.given_to_kind_label}{p.event_name ? ` · ${p.event_name}` : ''} · {fmtDay(p.created_at)}</span></>}
        actions={<>
          <button type="button" className="btn btn-secondary" onClick={() => setModal('edit')}>Edit details</button>
          {stopped
            ? <button type="button" className="btn btn-secondary" onClick={() => setModal('reactivate')}>Watch again</button>
            : <button type="button" className="btn btn-danger-ghost" onClick={() => setModal('retire')}>Stop watching</button>}
        </>} />

      {notice && <Alert tone="success">{notice}</Alert>}
      {leaks.length > 0 && own && (
        <Alert tone="danger">
          <strong>Someone else has a copy of this list.</strong> {leaks.length} email{leaks.length === 1 ? '' : 's'} reached its planted contacts from senders
          other than you. It may have leaked from {p.given_to}, or been passed on by someone with access. We marked the senders as a confirmed scam everywhere we protect and never replied.
        </Alert>
      )}
      {leaks.length > 0 && !own && (
        <Alert tone="danger">
          <strong>This list has been used by someone else.</strong> {leaks.length} email{leaks.length === 1 ? '' : 's'} reached its planted contacts from senders
          other than {p.given_to}. It has been passed on, sold or leaked. We marked the senders as a confirmed scam everywhere we protect and never replied.
        </Alert>
      )}

      <Section title="The contacts"
        description={own
          ? `Add these to ${p.given_to} like any other attendee — and to every other place you send event emails from (your registration platform and your marketing tool). Don’t mark them as test contacts or put them in a separate segment: they must get exactly what your attendees get.`
          : 'Add these to the list before you share it, scattered among the real ones. They don’t exist anywhere else, so never use them yourself.'}
        actions={contacts.length > 0 && (
          <span className="head-buttons">
            <CopyButton text={tsvOf(contacts)} label="Copy for a spreadsheet" />
            <CopyButton text={csvOf(contacts)} label="Copy as CSV" />
            <a className="btn btn-secondary btn-small" href={`/api${base}/contacts.csv`} download>Download CSV</a>
          </span>
        )} flush>
        <div className="table-scroll">
          <table className="table table-stack">
            <thead><tr><th>Name</th><th>Job title</th><th>Company</th><th>Email</th><th>Emails received</th></tr></thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id}>
                  <td data-label="Name">{c.first_name} {c.last_name}</td>
                  <td data-label="Job title">{c.job_title}</td>
                  <td data-label="Company">{c.company}</td>
                  <td data-label="Email"><span className="mono break">{c.address}</span></td>
                  <td data-label="Emails received">{c.hit_count ? `${c.hit_count} · last ${fmtDateTime(c.last_hit_at)}` : <span className="muted">None</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Emails to them" flush={hits.length > 0}
        description={own
          ? 'Every email that reached these contacts. Yours (proven to come from your domains) are recorded as official, so anyone who forwards us a copy is told it’s genuine. From anyone else, the list has leaked.'
          : `Every email from someone other than ${p.given_to}'s approved senders: each one means the list has travelled. Proven emails from approved senders are expected, so we count them and delete them.`}>
        {!own && p.approved_dropped > 0 && (
          <p className="muted small" style={{ margin: '0 0 12px' }}>
            {p.approved_dropped} email{p.approved_dropped === 1 ? '' : 's'} from approved senders received and deleted{p.last_approved_at ? `, most recently ${fmtDateTime(p.last_approved_at)}` : ''}.
          </p>
        )}
        {hits.length === 0 ? <EmptyState title="Nothing yet">{own ? 'Your next email to this list will show up here, marked as official.' : 'We’ll email you the moment one arrives.'}</EmptyState> : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Received</th><th>Email</th><th>To</th><th>What it means</th></tr></thead>
              <tbody>
                {hits.map((h) => (
                  <tr key={h.id}>
                    <td data-label="Received" className="nowrap">{fmtDateTime(h.received_at)}</td>
                    <td data-label="Email">
                      <Link className="strong-link" to={`/inbox/${h.message_id}`}>{h.subject || '(no subject)'}</Link>
                      <div className="muted small">From {h.from_name ? `${h.from_name} ` : ''}{h.from_address ? `<${h.from_address}>` : ''}</div>
                    </td>
                    <td data-label="To"><span className="mono small break">{h.to}</span></td>
                    <td data-label="What it means">{h.expected
                      ? (own ? <Pill tone="good">Yours: recorded as official</Pill> : <Pill tone="info">Expected: {p.given_to}</Pill>)
                      : <Pill tone="bad">Leaked: someone else</Pill>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Details">
        <Facts items={[
          own ? ['List', p.given_to] : ['Given to', `${p.given_to} (${p.given_to_kind_label})`],
          own
            ? ['Sends from', <>Your own domains{p.allowed_domains.length ? `, plus ${p.allowed_domains.join(', ')}` : ''}</>]
            : ['Approved senders', p.allowed_domains.length === 0
              ? <span className="muted">None: any email to these contacts means the list travelled</span>
              : p.allowed_domains.length <= 6 ? p.allowed_domains.join(', ')
              : <details><summary>{p.allowed_domains.length} domains</summary><p className="mono small break" style={{ marginTop: 8 }}>{p.allowed_domains.join(', ')}</p></details>],
          ['Note', p.note || '—'],
          ['Made', fmtDateTime(p.created_at)],
        ]} />
      </Section>

      {modal === 'edit' && <EditModal base={base} planting={p} kinds={data.kinds} onClose={() => setModal(null)} onSaved={() => { setModal(null); setNotice('Saved.'); load(); }} />}
      {modal === 'retire' && (
        <ConfirmModal title="Stop watching this list?" confirmLabel="Stop watching" danger onClose={() => setModal(null)}
          onConfirm={async () => { await api(`${base}/retire`, { method: 'POST' }); setModal(null); setNotice('Stopped. Mail to these contacts is refused from now on.'); await load(); }}>
          <p>Mail to its contacts will be refused from now on, so you won&rsquo;t hear if the list travels later. Lists are often resold months afterwards, so only stop if you need to.</p>
        </ConfirmModal>
      )}
      {modal === 'reactivate' && (
        <ConfirmModal title="Watch this list again?" confirmLabel="Watch again" onClose={() => setModal(null)}
          onConfirm={async () => { await api(`${base}/reactivate`, { method: 'POST' }); setModal(null); setNotice('Watching again.'); await load(); }}>
          <p>Mail to its contacts is accepted again, and you&rsquo;ll be told about every email.</p>
        </ConfirmModal>
      )}
    </div>
  );
}
